/**
 * VPN device audit timeline — the one row shape every audit source normalises to.
 *
 * Built ONCE in inventory-core (`buildDeviceAuditTimeline`) and served by both the
 * api's `vpnPeerTimeline` GraphQL query (the web VPN Peers rail) and the MCP, so the
 * two surfaces cannot disagree about what happened to a device. The GraphQL enums
 * `VpnTimelineKind` / `VpnTimelineOutcome` are registered from these arrays.
 */

/**
 * - `peer_registered` — the peer record was created (SecureLink IP allocated).
 * - `peer_updated` — the peer record changed after creation (re-provision, region move, key churn).
 * - `status` — a SecureLink status (a transition, or the current value as a fallback).
 * - `connection` — a staff member reached (or failed to reach) the device over ssh/adb/vnc.
 * - `provisioning` — a provisioning era: provision / retry / deprovision calls under one key.
 * - `placement` — a hub move: mdm-api asked the device to move, or the device answered.
 * - `relay` — a TCP-relay (wg-relay) session started or ended.
 */
export const PEER_TIMELINE_KINDS = [
  'peer_registered',
  'peer_updated',
  'status',
  'connection',
  'provisioning',
  'placement',
  'relay',
] as const;
export type PeerTimelineKind = (typeof PEER_TIMELINE_KINDS)[number];

export const PEER_TIMELINE_OUTCOMES = ['ok', 'fail', 'neutral'] as const;
export type PeerTimelineOutcome = (typeof PEER_TIMELINE_OUTCOMES)[number];

/** One row of a device peer's timeline. `detail` is formatted server-side on purpose. */
export interface PeerTimelineEvent {
  ts: Date;
  kind: PeerTimelineKind;
  /** Who did it, when known (device name, `mdm-api`, `wg-orchestrator`, a staff email). Never guessed. */
  actor?: string;
  /** The rest of the sentence after the actor — a verb phrase, not a label. */
  title: string;
  detail?: string;
  outcome: PeerTimelineOutcome;
}

/** Reach reliability over the window — staff connection attempts only. */
export interface PeerTimelineSummary {
  windowDays: number;
  attempts: number;
  established: number;
  /** Undefined when there were no attempts — never 0% for "nobody tried". */
  successPercent?: number;
}

export interface PeerTimeline {
  /** Newest first. */
  events: PeerTimelineEvent[];
  summary: PeerTimelineSummary;
  /** False when the connection-audit source is not configured (timeline is lifecycle-only). */
  auditAvailable: boolean;
}
